// components/workspace-timeline/WorkspaceTimelinePendingTracks.tsx
import React, { useCallback, useMemo } from 'react';
import { RefreshCw } from '@/components/ui/lucide-memo';
import type { PendingVideoGenerationTrack } from '#lib/timeline-generation-placeholders';
import { clamp } from './presentation';

interface WorkspaceTimelinePendingTrackItem {
  taskId: string;
  label: string;
  title: string;
  startPercent: number;
  widthPercent: number;
}

interface WorkspaceTimelinePendingTracksProps {
  tracks: PendingVideoGenerationTrack[];
  timelineRenderRangeStartMs: number;
  timelineRenderRangeMs: number;
  laneLeftColumnWidthPx: number;
  onPendingTrackContextMenu: (event: React.MouseEvent, taskId: string) => void;
}

interface WorkspaceTimelinePendingTrackRowProps extends WorkspaceTimelinePendingTrackItem {
  laneLeftColumnWidthPx: number;
  onContextMenu: (event: React.MouseEvent, taskId: string) => void;
}

const TIMELINE_PENDING_TRACK_ICON_CELL_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '7px',
  paddingLeft: '3px',
  color: '#8E8E8E',
};
const TIMELINE_PENDING_TRACK_CAPTION_STYLE: React.CSSProperties = { fontSize: 13 };
const TIMELINE_PENDING_TRACK_LANE_STYLE: React.CSSProperties = {
  borderRadius: '6px',
  border: '1px dashed rgba(236,236,236,0.2)',
  backgroundColor: 'rgba(255,255,255,0.02)',
};
const TIMELINE_PENDING_TRACK_BLOCK_BASE_STYLE: React.CSSProperties = {
  position: 'absolute',
  minWidth: '34px',
  top: '1px',
  bottom: '1px',
  borderRadius: '6px',
  backgroundImage: 'linear-gradient(90deg, #3A3A3A, #4A4A4A, #3A3A3A), repeating-linear-gradient(-45deg, rgba(255,255,255,0.08) 0 5px, rgba(255,255,255,0.02) 5px 10px)',
  opacity: 0.9,
  overflow: 'hidden',
};
const TIMELINE_PENDING_TRACK_LABEL_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: '8px',
  top: '50%',
  transform: 'translateY(-50%)',
  maxWidth: 'calc(100% - 16px)',
  fontSize: 12,
  lineHeight: 1.2,
  color: '#ECECEC',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const WorkspaceTimelinePendingTrackRow = React.memo(({
  taskId,
  label,
  title,
  startPercent,
  widthPercent,
  laneLeftColumnWidthPx,
  onContextMenu,
}: WorkspaceTimelinePendingTrackRowProps) => {
  const rootStyle = useMemo<React.CSSProperties>(() => ({
    gridTemplateColumns: `${laneLeftColumnWidthPx}px minmax(0,1fr)`,
  }), [laneLeftColumnWidthPx]);
  const trackBlockStyle = useMemo<React.CSSProperties>(() => ({
    ...TIMELINE_PENDING_TRACK_BLOCK_BASE_STYLE,
    left: `${startPercent}%`,
    width: `${widthPercent}%`,
  }), [startPercent, widthPercent]);
  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    onContextMenu(event, taskId);
  }, [onContextMenu, taskId]);

  return (
    <div
      className="grid w-full items-center gap-2"
      onContextMenu={handleContextMenu}
      style={rootStyle}
    >
      <div style={TIMELINE_PENDING_TRACK_ICON_CELL_STYLE} title="Generating video">
        <RefreshCw size={14} strokeWidth={2} className="animate-spin" />
        <span style={TIMELINE_PENDING_TRACK_CAPTION_STYLE}>Generating</span>
      </div>
      <div className="relative h-7 flex-1 overflow-hidden" style={TIMELINE_PENDING_TRACK_LANE_STYLE}>
        <div className="pointer-events-none animate-pulse" style={trackBlockStyle} title={title}>
          <div className="select-none" style={TIMELINE_PENDING_TRACK_LABEL_STYLE}>
            {label}
          </div>
        </div>
      </div>
    </div>
  );
});

WorkspaceTimelinePendingTrackRow.displayName = 'WorkspaceTimelinePendingTrackRow';

export const WorkspaceTimelinePendingTracks = React.memo(({
  tracks,
  timelineRenderRangeStartMs,
  timelineRenderRangeMs,
  laneLeftColumnWidthPx,
  onPendingTrackContextMenu,
}: WorkspaceTimelinePendingTracksProps) => {
  const pendingTrackItems = useMemo<WorkspaceTimelinePendingTrackItem[]>(() => {
    return tracks.map((pendingTrack) => {
      const rawStartPercent = timelineRenderRangeMs > 0
        ? ((pendingTrack.startMs - timelineRenderRangeStartMs) / timelineRenderRangeMs) * 100
        : 0;
      const rawWidthPercent = timelineRenderRangeMs > 0
        ? Math.max((pendingTrack.durationMs / timelineRenderRangeMs) * 100, 0.8)
        : 0.8;
      const outputLabel =
        pendingTrack.outputWidth && pendingTrack.outputHeight
          ? `${pendingTrack.outputWidth}x${pendingTrack.outputHeight}`
          : 'pending output';
      const durationLabel = `${Math.max(0.1, pendingTrack.durationMs / 1000).toFixed(1)}s`;
      return {
        taskId: pendingTrack.taskId,
        label: pendingTrack.label,
        title: `${pendingTrack.model} generating ${outputLabel}, ${durationLabel}`,
        startPercent: clamp(rawStartPercent, 0, 100),
        widthPercent: clamp(rawWidthPercent, 0, 100),
      };
    });
  }, [timelineRenderRangeMs, timelineRenderRangeStartMs, tracks]);

  return (
    <>
      {pendingTrackItems.map((item) => (
        <WorkspaceTimelinePendingTrackRow
          key={`pending-video-generation-${item.taskId}`}
          taskId={item.taskId}
          label={item.label}
          title={item.title}
          startPercent={item.startPercent}
          widthPercent={item.widthPercent}
          laneLeftColumnWidthPx={laneLeftColumnWidthPx}
          onContextMenu={onPendingTrackContextMenu}
        />
      ))}
    </>
  );
});

WorkspaceTimelinePendingTracks.displayName = 'WorkspaceTimelinePendingTracks';
